import React, { useCallback, useMemo } from 'react';
import { MicroProductScreenView } from '@/experiences/sales-portal/domain/microproducts/screens/micro-product-screen-view';
import {
  MicroProductsSlot,
  useMicroProductsLoader,
} from '@domain/microproducts/loaders/use-microproducts-loader';
import { NavigateToMicroproductPayload } from '@domain/microproducts/components/micro-product-webview-types';
import { navigateToSalesPortalRoute } from '@/experiences/sales-portal/sales-portal-route-config';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import { SalesPortalStackParams } from '@/navigators/types';
import { StackScreenProps } from '@react-navigation/stack';

export type PricingMicroProductScreenProps = StackScreenProps<
  SalesPortalStackParams,
  typeof SALES_PORTAL_ROUTES.PricingMicroProduct
>;

/**
 * Renders a pricing-scoped microproduct inside the pricing sidebar shell.
 *
 * Wraps the shared `MicroProductScreenView`, but lives in the pricing stack so
 * the Pricing sidebar group stays visible and the supplierUUID is preserved in
 * the URL on microproduct→microproduct navigation. Reached only from the
 * pricing sub-navigation (the `PRICING_SIDEBAR` microproducts slot) — never the
 * top-level sales-hub sidebar.
 */
export const PricingMicroProductScreen: React.FC<
  PricingMicroProductScreenProps
> = ({ navigation, route }) => {
  // pricing-impact is org-keyed end-to-end (calyx + salsa carry no supplier
  // dimension), so the iframe is identical across suppliers and reused on
  // supplier switch.
  const productId = route.params?.productId;

  const { slots } = useMicroProductsLoader();
  // Raw slot membership (not the manifest-filtered list) — a pricing product
  // that isn't deployed yet still belongs on the pricing route.
  const pricingProductIds = useMemo(
    () =>
      new Set(
        slots
          .find((s) => s.slot === MicroProductsSlot.PRICING_SIDEBAR)
          ?.products.map((p) => p.id) ?? [],
      ),
    [slots],
  );

  const handleNavigateToMicroproduct = useCallback(
    (payload: NavigateToMicroproductPayload) => {
      // Keep each product in its proper shell. Pricing-slot products stay on
      // the supplier-scoped pricing route; anything else falls back to Base so
      // it doesn't render behind the pricing gate at a URL it doesn't own. (No
      // SPA emits cross-slot NAVIGATE_MICROPRODUCT today — this is defensive.)
      if (pricingProductIds.has(payload.productId)) {
        navigateToSalesPortalRoute(
          navigation,
          SALES_PORTAL_ROUTES.PricingMicroProduct,
          {
            productId: payload.productId,
            ...(payload.params && { microproductParams: payload.params }),
          },
        );
        return;
      }
      navigateToSalesPortalRoute(navigation, SALES_PORTAL_ROUTES.Base, {
        productId: payload.productId,
        ...(payload.params && { microproductParams: payload.params }),
      });
    },
    [navigation, pricingProductIds],
  );

  return (
    <MicroProductScreenView
      microproductParams={route.params?.microproductParams}
      mpRoute={route.params?.mpRoute}
      navigation={navigation}
      onNavigateToMicroproduct={handleNavigateToMicroproduct}
      productId={productId}
      slot={MicroProductsSlot.PRICING_SIDEBAR}
      Template={SalesHubShellTemplate}
    />
  );
};
